<template>
  <BaseModal ref="modal">
    <UiModalDialog>
      <template #header>
        <BaseIcon name="mdi/account-multiple-plus-outline" />
        新規プロジェクトの作成
      </template>
      <div class="jps-modal-content">
        <form @submit.prevent="ok()">
          <div class="jps-field">
            <label class="jps-label">
              ID
              <input class="jps-input" type="text" v-model="project.id" />
            </label>
            <p class="jps-jps-message jps-red" v-if="idMsg">{{ idMsg }}</p>
            <p class="jps-jps-message jps-red" v-if="exists">
              そのIDは既に存在しています。別のIDにしてください。
            </p>
          </div>
          <ValidationForm :rule="rule" :target="project" @valid="(v) => valid = v">
            <p class="jps-form-help">ID以外は後から変更出来ます。</p>
          </ValidationForm>
        </form>
      </div>
      <template #buttons>
        <button @click="modal.reject()">{{ ls(dapLangGlobal.cancel) }}</button>
        <button @click="ok()" :disabled="!allValid">作成する</button>
      </template>
    </UiModalDialog>
  </BaseModal>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { BaseModal } from '#components'

const { ls } = useI18n()
const { parentIdExists } = useDataStore()

interface Props {
}
const props = withDefaults(defineProps<Props>(), {
})

const modal = ref<InstanceType<typeof BaseModal>>()
const project: Ref<Organization> = ref()
const openAwait = async () => {
  valid.value = false
  exists.value = false

  project.value = {
    id: '',
    name: { ja: '', en: '', yomi: '' },
    description: { ja: '', en: '' },
    url: '',
    image: null,
  }
  return await modal.value.openAwait<Organization>()
}
defineExpose({ openAwait })

const rule: ValidationRule = {
  id: 'jps-project',
  strict: true,
  fields: {
    name: {
      field: 'name',
      type: 'object',
      jaMandatory: true,
      enMandatory: true,
      yomiMandatory: true,
      objRuleName: 'jps-lang_string_yomi',
      label: {
        ja: 'プロジェクト名',
        en: 'Name',
      },
    },
    description: {
      field: 'description',
      type: 'object',
      mandatory: false,
      objRuleName: 'jps-lang_string',
      label: {
        ja: '説明',
        en: 'Description',
      },
    },
    url: {
      field: 'url',
      type: 'string',
      mandatory: false,
      label: {
        ja: 'HP等のURL',
        en: 'HP URL',
      },
    },
    image: {
      field: 'image',
      type: 'object',
      mandatory: false,
      objRuleName: 'jps-image_selection',
      label: {
        ja: '代表画像/ロゴ',
        en: 'Representative Image/Logo',
      },
    },
  },
}

const valid:Ref<boolean> = ref(false)
const exists:Ref<Boolean> = ref(false)

watch(
  () => project.value?.id,
  async () => {
    if (project.value.id) {
      exists.value = await parentIdExists(project.value.id)
    }
  }
)

const idMsg = computed(() => {
  if (project.value.id) {
    if (!project.value.id.match(/^[a-zA-Z][_0-9a-zA-Z]{2,19}$/)) {
      return 'IDは3～20文字の半角アルファベット大文字小文字、数字、_で構成してください。'
    }
    return null
  } else {
    return 'IDは必須項目です。'
  }
})

const allValid = computed(() => {
  return idMsg.value === null && !exists.value && valid.value
})

const ok = () => {
  modal.value.resolve(project.value)
}
</script>

<style lang="scss" scoped>
</style>
